import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const field = "w-full bg-race-bg border border-race-line rounded-md px-3 py-2.5 text-sm text-race-txt outline-none focus:border-race-volt transition-colors";
const label = "block text-xs uppercase tracking-wide text-race-sub mb-1.5";

export default function AdvAuth({ mode = "register" }) {
  const nav = useNavigate();
  const { login, setUser } = useAuth();
  const isLogin = mode === "login";
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState({ first_name: "", last_name: "", business_name: "", email: "", password: "", confirm: "", website: "", phone: "", agree: false });
  const on = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (isLogin) {
      setBusy(true);
      const r = await login(f.email, f.password);
      setBusy(false);
      if (!r.ok) return setErr(r.error);
      if (r.user?.role !== "advertiser") { setErr("This isn't an advertiser account."); return; }
      nav("/advertiser");
      return;
    }
    if (f.password !== f.confirm) return setErr("Passwords don't match.");
    if (!f.agree) return setErr("Please agree to the advertising terms.");
    setBusy(true);
    try {
      const { data } = await api.post("/adv/auth/register", {
        first_name: f.first_name, last_name: f.last_name, business_name: f.business_name,
        email: f.email, password: f.password, website: f.website, phone: f.phone, agree: f.agree });
      setUser(data);
      toast.success("Welcome to Text Racing Advertising");
      nav("/advertiser");
    } catch (e2) { setErr(apiError(e2.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-race-bg text-race-txt flex flex-col">
      <div className="px-6 lg:px-10 py-5 border-b border-race-line flex items-baseline gap-2">
        <Link to="/" className="font-heading text-2xl tracking-tight">TEXT RACING</Link>
        <span className="text-[10px] uppercase tracking-[0.2em] text-race-volt font-semibold">Advertiser</span>
      </div>
      <div className="flex-1 grid lg:grid-cols-2">
        <div className="hidden lg:flex flex-col justify-center px-16 border-r border-race-line bg-[#0c0c0f]">
          <h1 className="font-heading text-5xl leading-[1.05] tracking-tight">Advertise on<br />Text Racing.</h1>
          <p className="mt-5 text-race-sub max-w-md text-base">Reach an engaged audience of competitive texters and trivia racers. Launch a campaign in minutes — you're only billed for what you deliver.</p>
          <ul className="mt-8 space-y-2 text-sm text-race-sub">
            <li>• Interest-based ad personalization</li>
            <li>• AI-assisted ad creation</li>
            <li>• Weekly pay-as-you-go billing, no upfront lump sum</li>
          </ul>
        </div>
        <div className="flex items-center justify-center px-6 py-10">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="adv-auth-form">
            <h2 className="font-heading text-3xl tracking-tight mb-1">{isLogin ? "Sign in" : "Create advertiser account"}</h2>
            <p className="text-race-sub text-sm mb-6">{isLogin ? "Access your advertising dashboard." : "Tell us about your business to get started."}</p>
            {err && <div className="mb-4 rounded-md border border-race-red/50 bg-race-red/10 px-3 py-2 text-sm text-race-red" data-testid="adv-auth-error">{err}</div>}
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><label className={label}>First name</label><input className={field} value={f.first_name} onChange={on("first_name")} required data-testid="adv-first-name" /></div>
                <div><label className={label}>Last name</label><input className={field} value={f.last_name} onChange={on("last_name")} required data-testid="adv-last-name" /></div>
              </div>
            )}
            {!isLogin && <div className="mb-3"><label className={label}>Business / Organization</label><input className={field} value={f.business_name} onChange={on("business_name")} required data-testid="adv-business-name" /></div>}
            <div className="mb-3"><label className={label}>{isLogin ? "Email" : "Business email"}</label><input type="email" className={field} value={f.email} onChange={on("email")} required data-testid="adv-email" /></div>
            <div className="mb-3"><label className={label}>Password</label><input type="password" className={field} value={f.password} onChange={on("password")} required data-testid="adv-password" /></div>
            {!isLogin && <div className="mb-3"><label className={label}>Confirm password</label><input type="password" className={field} value={f.confirm} onChange={on("confirm")} required data-testid="adv-confirm" /></div>}
            {!isLogin && (
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div><label className={label}>Website (optional)</label><input className={field} value={f.website} onChange={on("website")} placeholder="https://" data-testid="adv-website" /></div>
                <div><label className={label}>Phone (optional)</label><input className={field} value={f.phone} onChange={on("phone")} data-testid="adv-phone" /></div>
              </div>
            )}
            {!isLogin && (
              <label className="flex items-start gap-2 mb-5 text-sm text-race-sub cursor-pointer">
                <input type="checkbox" checked={f.agree} onChange={on("agree")} className="mt-0.5" data-testid="adv-agree" />
                <span>I agree to the advertising terms and policies.</span>
              </label>
            )}
            <button type="submit" disabled={busy} data-testid="adv-auth-submit"
              className="w-full bg-race-volt text-race-bg font-semibold py-3 rounded-md hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy ? "Please wait…" : isLogin ? "SIGN IN" : "CREATE ADVERTISER ACCOUNT"}
            </button>
            <div className="mt-5 text-center text-sm text-race-sub">
              {isLogin ? (<>New here? <Link to="/advertiser/register" className="text-race-volt hover:underline" data-testid="adv-to-register">Create an advertiser account</Link></>)
                       : (<>Already have an advertiser account? <Link to="/advertiser/login" className="text-race-volt hover:underline" data-testid="adv-to-login">Sign in</Link></>)}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
